import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface NavigationItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: string | number;
  children?: NavigationItem[];
  isNew?: boolean;
  requiresPermission?: string;
  apiEndpoint?: string;
  refreshInterval?: number;
}

export interface DashboardMetrics {
  unreadMessages: number;
  pendingTasks: number;
  activeProjects: number;
  notifications: number;
  systemAlerts: number;
  teamMembers: number;
  clients: number;
  revenue: number;
  lastUpdated: string;
}

export interface NavigationStats {
  userActivity: {
    lastLogin: string;
    activeSession: string;
    loginCount: number;
  };
  quickActions: Array<{
    label: string;
    path: string;
    icon: React.ReactNode;
    count?: number;
  }>;
  recentItems: Array<{
    title: string;
    path: string;
    type: 'project' | 'task' | 'message' | 'file';
    timestamp: string;
  }>;
  favoriteItems: Array<{
    title: string;
    path: string;
    type: string;
  }>;
}

/**
 * Enhanced Navigation Hook with API Integration
 * Provides dynamic navigation items with real-time data
 */
export const useEnhancedNavigation = () => {
  const { user } = useAuth();
  
  // Disable metrics fetching during development to reduce noise
  const DISABLE_METRICS = process.env.NODE_ENV === 'development' || true;
  
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    unreadMessages: 0,
    pendingTasks: 0,
    activeProjects: 0,
    notifications: 0,
    systemAlerts: 0,
    teamMembers: 0,
    clients: 0,
    revenue: 0,
    lastUpdated: new Date().toISOString(),
  });
  
  const [stats, setStats] = useState<NavigationStats>({
    userActivity: {
      lastLogin: '',
      activeSession: '',
      loginCount: 0,
    },
    quickActions: [],
    recentItems: [],
    favoriteItems: [],
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch dashboard metrics from API
   */
  const fetchDashboardMetrics = useCallback(async () => {
    // Skip metrics fetching during development
    if (DISABLE_METRICS) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch('http://localhost:8000/api/v1/dashboard/metrics', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMetrics(data.metrics || metrics);
        setStats(data.stats || stats);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard metrics:', err);
      setError('Failed to load navigation data');
    } finally {
      setLoading(false);
    }
  }, [DISABLE_METRICS, metrics, stats]);

  /**
   * Update metrics for specific navigation items
   */
  const updateMetric = useCallback((key: keyof DashboardMetrics, value: number) => {
    setMetrics(prev => ({
      ...prev,
      [key]: value,
      lastUpdated: new Date().toISOString(),
    }));
  }, []);

  /**
   * Mark item as read/completed to update badges
   */
  const markAsRead = useCallback(async (type: string, count: number = 1) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      await fetch('http://localhost:8000/api/v1/dashboard/mark-read', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type, count }),
      });

      // Update local state
      switch (type) {
        case 'messages':
          updateMetric('unreadMessages', Math.max(0, metrics.unreadMessages - count));
          break;
        case 'tasks':
          updateMetric('pendingTasks', Math.max(0, metrics.pendingTasks - count));
          break;
        case 'notifications':
          updateMetric('notifications', Math.max(0, metrics.notifications - count));
          break;
      }
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, [metrics, updateMetric]);

  /**
   * Add item to favorites
   */
  const addToFavorites = useCallback(async (item: { title: string; path: string; type: string }) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      await fetch('http://localhost:8000/api/v1/dashboard/favorites', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      });

      setStats(prev => ({
        ...prev,
        favoriteItems: [...prev.favoriteItems, item],
      }));
    } catch (err) {
      console.error('Failed to add to favorites:', err);
    }
  }, []);

  /**
   * Get role-specific quick actions with live data
   */
  const getQuickActions = useCallback(() => {
    const role = user?.role;
    const baseActions = {
      admin: [
        { label: 'Create User', path: '/dashboard/users/create', icon: '👤', count: metrics.teamMembers },
        { label: 'New Project', path: '/dashboard/projects/create', icon: '📋', count: metrics.activeProjects },
        { label: 'System Monitor', path: '/dashboard/monitor', icon: '⚡', count: metrics.systemAlerts },
        { label: 'Reports', path: '/dashboard/analytics', icon: '📊', count: metrics.clients },
      ],
      project_manager: [
        { label: 'Assign Tasks', path: '/dashboard/team/tasks', icon: '✅', count: metrics.pendingTasks },
        { label: 'Project Timeline', path: '/dashboard/projects/timeline', icon: '📅', count: metrics.activeProjects },
        { label: 'Team Meeting', path: '/dashboard/team/meetings', icon: '👥', count: metrics.teamMembers },
        { label: 'Client Update', path: '/dashboard/clients/updates', icon: '💬', count: metrics.unreadMessages },
      ],
      developer: [
        { label: 'My Tasks', path: '/dashboard/tasks/active', icon: '🔨', count: metrics.pendingTasks },
        { label: 'Code Review', path: '/dashboard/tasks/reviews', icon: '👁️', count: 0 },
        { label: 'Deploy', path: '/dashboard/tools/deploy', icon: '🚀', count: 0 },
        { label: 'Bug Reports', path: '/dashboard/bugs/assigned', icon: '🐛', count: 0 },
      ],
      client: [
        { label: 'View Progress', path: '/dashboard/projects/active', icon: '📈', count: metrics.activeProjects },
        { label: 'Messages', path: '/dashboard/communication/messages', icon: '💬', count: metrics.unreadMessages },
        { label: 'Invoices', path: '/dashboard/billing/invoices', icon: '💰', count: 0 },
        { label: 'Support', path: '/dashboard/support', icon: '🆘', count: 0 },
      ],
    };

    return baseActions[role as keyof typeof baseActions] || [];
  }, [user?.role, metrics]);

  // Initialize and set up real-time updates
  useEffect(() => {
    fetchDashboardMetrics();

    // Skip periodic refresh during development
    if (DISABLE_METRICS) {
      return;
    }

    // Set up periodic refresh for metrics
    const interval = setInterval(fetchDashboardMetrics, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [fetchDashboardMetrics, DISABLE_METRICS]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    // Skip WebSocket connections during development
    if (DISABLE_METRICS) {
      return;
    }
    
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:8000'}/ws/dashboard?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'metrics_update':
            setMetrics(prev => ({ ...prev, ...data.metrics }));
            break;
          case 'new_message':
            updateMetric('unreadMessages', metrics.unreadMessages + 1);
            break;
          case 'task_assigned':
            updateMetric('pendingTasks', metrics.pendingTasks + 1);
            break;
          case 'notification':
            updateMetric('notifications', metrics.notifications + 1);
            break;
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }, [DISABLE_METRICS, metrics, updateMetric]);

  return {
    metrics,
    stats,
    loading,
    error,
    updateMetric,
    markAsRead,
    addToFavorites,
    getQuickActions,
    refreshMetrics: fetchDashboardMetrics,
  };
};